import { Field, FieldError, FieldHint, FieldLabel } from "@nt/ui/field";
import { buttonVariants, inputVariants } from "@nt/ui/recipes";
import { useQueryClient } from "@tanstack/react-query";
import { BadgeDollarSign, ChartLine, Link2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import { authPost, messageOf } from "@/lib/api";

/** Seconds before "Send a new code" works again. */
const RESEND_COOLDOWN_SECONDS = 60;

/** Keep the digits of a typed or pasted code, at most six ("123 456" → "123456"). */
export function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 6);
}

/** A countdown for "Send a new code": `start()` arms it, `seconds` counts to 0. */
export function useResendCooldown(total = RESEND_COOLDOWN_SECONDS) {
  const [until, setUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until === null) return;
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= until) {
        setUntil(null);
        window.clearInterval(id);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [until]);
  const start = useCallback(() => {
    const t = Date.now();
    setNow(t);
    setUntil(t + total * 1000);
  }, [total]);
  const seconds =
    until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));
  return { seconds, start };
}

const VALUE_LINES = [
  { icon: Link2, text: "Share your link and code, and see every click." },
  {
    icon: ChartLine,
    text: "Follow sign-ups and fees as your customers buy.",
  },
  {
    icon: BadgeDollarSign,
    text: "Get paid every month through Wise.",
  },
] as const;

/**
 * The signed-out frame: a forest brand panel (mark, "Partner portal", three
 * value lines) beside the content on desktop; one column under 768px with a
 * short brand header.
 */
export function AuthSplit({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen w-full flex-col bg-background md:flex-row">
      <aside
        data-slot="brand-panel"
        className="flex flex-col gap-6 bg-primary px-4 py-6 text-primary-foreground md:w-[44%] md:max-w-[560px] md:justify-between md:px-12 md:py-12"
      >
        <div className="flex items-center gap-3">
          <img
            src="/nonresidentTaxIcon.svg"
            alt=""
            width={36}
            height={36}
            className="size-9 rounded-control"
          />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold">Nonresident Tax</span>
            <span className="text-compact opacity-80">Partner portal</span>
          </div>
        </div>
        <div className="hidden flex-col gap-6 md:flex">
          <h1 className="text-3xl font-semibold tracking-tight text-balance">
            Partner portal
          </h1>
          <ul className="flex flex-col gap-4">
            {VALUE_LINES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3 text-base">
                <Icon aria-hidden className="mt-0.5 size-5 shrink-0" />
                <span className="opacity-90">{text}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="hidden text-compact opacity-80 md:block">
          Questions? Write to support@nonresident.tax.
        </p>
      </aside>
      <main className="flex flex-1 items-start justify-center px-4 py-8 md:items-center md:px-12 md:py-12">
        <div className="flex w-full max-w-md flex-col gap-6">{children}</div>
      </main>
    </div>
  );
}

/** The form card on the right of `AuthSplit`. */
export function AuthCard({
  title,
  description,
  children,
}: {
  title: string;
  description: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-6 rounded-card border border-border bg-card p-6 shadow-card sm:p-8">
      <div className="flex flex-col gap-1.5">
        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

export function EmailStep({
  email,
  onEmail,
  onSubmit,
  busy,
  error,
}: {
  email: string;
  onEmail: (v: string) => void;
  onSubmit: () => void;
  busy: boolean;
  error: string | null;
}) {
  return (
    <AuthCard
      title="Sign in"
      description="We email you a 6-digit code. Use the email address you applied with."
    >
      <form
        className="grid gap-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            aria-invalid={error ? true : undefined}
            className={inputVariants()}
          />
        </Field>
        {error ? <FieldError role="alert">{error}</FieldError> : null}
        <button
          type="submit"
          disabled={busy || !email.trim()}
          className={buttonVariants({ size: "lg" })}
        >
          {busy ? "Sending…" : "Email me a code"}
        </button>
      </form>
    </AuthCard>
  );
}

export function CodeStep({
  email,
  otp,
  onOtp,
  onSubmit,
  onResend,
  onChangeEmail,
  resendIn,
  busy,
  error,
  notice,
}: {
  email: string;
  otp: string;
  onOtp: (v: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  onChangeEmail: () => void;
  /** Seconds until "Send a new code" works again; 0 = now. */
  resendIn: number;
  busy: boolean;
  error: string | null;
  notice: string | null;
}) {
  return (
    <AuthCard
      title="Check your email"
      description={
        <>
          We sent a 6-digit code to{" "}
          <span className="font-medium text-foreground">{email}</span>. It
          expires in a few minutes.
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Field>
          <FieldLabel htmlFor="otp">6-digit code</FieldLabel>
          <input
            id="otp"
            name="otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            autoFocus
            value={otp}
            onChange={(e) => onOtp(digitsOnly(e.target.value))}
            onPaste={(e) => {
              const pasted = digitsOnly(e.clipboardData.getData("text"));
              if (pasted) {
                e.preventDefault();
                onOtp(pasted);
              }
            }}
            aria-invalid={error ? true : undefined}
            className={inputVariants({
              mono: true,
              className: "h-12 text-center text-xl tracking-[0.5em]",
            })}
          />
          <FieldHint>
            Paste the code or type it. Check spam if it is not there.
          </FieldHint>
        </Field>
        {error ? <FieldError role="alert">{error}</FieldError> : null}
        {notice ? (
          <p role="status" className="text-sm text-success">
            {notice}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={busy || otp.length !== 6}
          className={buttonVariants({ size: "lg" })}
        >
          {busy ? "Checking…" : "Sign in"}
        </button>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            className={buttonVariants({ variant: "link", size: "sm" })}
            onClick={onChangeEmail}
          >
            Use another email
          </button>
          <button
            type="button"
            disabled={busy || resendIn > 0}
            className={buttonVariants({ variant: "ghost", size: "sm" })}
            onClick={onResend}
          >
            {resendIn > 0
              ? `Send a new code in ${resendIn} s`
              : "Send a new code"}
          </button>
        </div>
      </form>
    </AuthCard>
  );
}

export function SignIn() {
  const client = useQueryClient();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const cooldown = useResendCooldown();

  async function sendCode(resend: boolean) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await authPost("email-otp/send-verification-otp", {
        email: email.trim(),
        type: "sign-in",
      });
      setStep("code");
      cooldown.start();
      if (resend) {
        setOtp("");
        setNotice("We sent a new code. Use the newest one.");
      }
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  async function verify() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await authPost("sign-in/email-otp", {
        email: email.trim(),
        otp: otp.trim(),
      });
      await client.invalidateQueries({ queryKey: ["session"] });
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthSplit>
      {step === "email" ? (
        <EmailStep
          email={email}
          onEmail={setEmail}
          onSubmit={() => void sendCode(false)}
          busy={busy}
          error={error}
        />
      ) : (
        <CodeStep
          email={email.trim()}
          otp={otp}
          onOtp={setOtp}
          onSubmit={() => void verify()}
          onResend={() => void sendCode(true)}
          onChangeEmail={() => {
            setStep("email");
            setOtp("");
            setError(null);
            setNotice(null);
          }}
          resendIn={cooldown.seconds}
          busy={busy}
          error={error}
          notice={notice}
        />
      )}
      <p className="text-center text-sm text-muted-foreground">
        Not a partner yet?{" "}
        <a
          className="font-medium text-primary underline-offset-4 hover:underline"
          href="https://nonresident.tax/partnership"
        >
          Apply at nonresident.tax/partnership
        </a>
        .
      </p>
    </AuthSplit>
  );
}
